import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { CSVLink } from "react-csv";
import CreatePOFormModal from "./CreatePOFormModal";
import AddToExistingPOModal from "./AddToExistingPOModal";

const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 320,
      width: 360,
    },
  },
};

const FILTER_DEFINITIONS = [
  { key: "selectedOnly", label: "Selected Only" },
  { key: "acceptedOnly", label: "Accepted Only" },
  { key: "pendingOnly", label: "Pending Only" },
  { key: "needsDecision", label: "Needs Decision" },
  { key: "overMax", label: "Over Max Offer" },
  { key: "overSuggested", label: "Over Suggested Buy" },
  { key: "lowConfidence", label: "Review Confidence" },
  { key: "lowSales", label: "Low Sales Count" },
  { key: "lowSellThrough", label: "Low Sell-through" },
  { key: "missingSummary", label: "Missing Summary" },
  { key: "missingPricing", label: "Missing Pricing" },
  { key: "poUnassigned", label: "Not In PO" },
];

const formatCurrency = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : "";
};

const formatPercent = (value, decimals = 1) => {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(decimals)}%` : "";
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const roundToTwo = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Number(number.toFixed(2)) : value;
};

const rowQuantity = (row) => {
  const quantity = Number(row.quantity_ordered);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
};

const rowOfferTotal = (row) => (Number(row.offer_price) || 0) * rowQuantity(row);
const rowPredictedTotal = (row) => (Number(row.predicted_sale_price) || 0) * rowQuantity(row);

const getRiskFlags = (row) => {
  const flags = [];
  const offerPrice = toNumber(row.offer_price);
  const maxOfferPrice = toNumber(row.max_offer_price);
  const suggestedBuyPrice = toNumber(row.suggested_buy_price);
  const confidenceScore = toNumber(row.confidence_score);
  const salesCount = toNumber(row.sales_count_30_actual ?? row.sales_count);
  const sellThroughRate = toNumber(row.sell_through_rate_30);

  if (!row.summary_id) {
    flags.push("Missing Summary");
  }
  if (!row.pricing_advice_id) {
    flags.push("Missing Pricing");
  }
  if (offerPrice != null && maxOfferPrice != null && offerPrice > maxOfferPrice) {
    flags.push("Over Max Offer");
  }
  if (offerPrice != null && suggestedBuyPrice != null && offerPrice > suggestedBuyPrice) {
    flags.push("Over Suggested Buy");
  }
  if (confidenceScore != null && confidenceScore <= 0) {
    flags.push("Review Confidence");
  }
  if (salesCount != null && salesCount < 3) {
    flags.push("Low Sales Count");
  }
  if (sellThroughRate != null && sellThroughRate < 35) {
    flags.push("Low Sell-through");
  }
  if (row.offer_status === "pending") {
    flags.push("Needs Decision");
  }
  if (row.purchase_order_id) {
    flags.push("In PO");
  }

  return flags;
};

const matchesFilter = (row, filterKey, selectedIds) => {
  const offerPrice = toNumber(row.offer_price);
  const maxOfferPrice = toNumber(row.max_offer_price);
  const suggestedBuyPrice = toNumber(row.suggested_buy_price);
  const confidenceScore = toNumber(row.confidence_score);
  const salesCount = toNumber(row.sales_count_30_actual ?? row.sales_count);
  const sellThroughRate = toNumber(row.sell_through_rate_30);

  switch (filterKey) {
    case "selectedOnly":
      return selectedIds.has(row.id);
    case "acceptedOnly":
      return row.offer_status === "accepted";
    case "pendingOnly":
      return row.offer_status === "pending";
    case "needsDecision":
      return row.offer_status === "pending";
    case "overMax":
      return offerPrice != null && maxOfferPrice != null && offerPrice > maxOfferPrice;
    case "overSuggested":
      return offerPrice != null && suggestedBuyPrice != null && offerPrice > suggestedBuyPrice;
    case "lowConfidence":
      return confidenceScore != null && confidenceScore <= 0;
    case "lowSales":
      return salesCount != null && salesCount < 3;
    case "lowSellThrough":
      return sellThroughRate != null && sellThroughRate < 35;
    case "missingSummary":
      return !row.summary_id;
    case "missingPricing":
      return !row.pricing_advice_id;
    case "poUnassigned":
      return !row.purchase_order_id;
    default:
      return true;
  }
};

const TotalsCard = ({ label, value, helperText }) => (
  <Paper variant="outlined" sx={{ p: 1.5, minWidth: 170 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
    {helperText ? (
      <Typography variant="caption" color="text.secondary">
        {helperText}
      </Typography>
    ) : null}
  </Paper>
);

const PrePOSummaryDialog = ({ open, onClose, onConfirm, actionLabel, summary }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{actionLabel}</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Review the selected purchase requests before continuing.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 1.5 }}>
        <TotalsCard label="Selected Rows" value={summary.selectedCount} />
        <TotalsCard label="Accepted Rows" value={summary.acceptedCount} />
        <TotalsCard label="Pending Rows" value={summary.pendingCount} />
        <TotalsCard label="Rejected Rows" value={summary.rejectedCount} />
        <TotalsCard label="Selected Quantity" value={summary.quantityTotal} />
        <TotalsCard label="Selected Offer Total" value={formatCurrency(summary.offerTotal)} />
        <TotalsCard label="Selected Predicted Total" value={formatCurrency(summary.predictedTotal)} />
        <TotalsCard label="Projected Profit" value={formatCurrency(summary.projectedProfit)} />
        <TotalsCard label="Already In PO" value={summary.alreadyInPOCount} />
      </Box>

      {summary.warnings.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Warnings
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {summary.warnings.map((warning) => (
              <Alert key={warning} severity="warning">
                {warning}
              </Alert>
            ))}
          </Box>
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" onClick={onConfirm}>
        Continue
      </Button>
    </DialogActions>
  </Dialog>
);

const normalizeRecommendedExclusions = (review) => {
  const exclusions = review?.recommended_exclusions_json;
  if (Array.isArray(exclusions)) return exclusions;
  if (typeof exclusions === "string" && exclusions.trim()) {
    try {
      const parsed = JSON.parse(exclusions);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.warn("Unable to parse recommended exclusions", err);
    }
  }
  return [];
};


const normalizeLotReviewList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.warn("Unable to parse lot review list", err);
    }
  }
  return [];
};

const formatLotReviewLabel = (value) => {
  if (!value) return "N/A";
  return String(value).replace(/_/g, " ");
};

const normalizeLotReviewIdentifier = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return String(numericValue);
  return String(value).trim().toLowerCase();
};

const normalizeLotReviewCardName = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

const lotReviewCardMatchesRow = (item, row) => {
  const stableKeys = [
    ["summary_id", row?.summary_id],
    ["purchase_request_id", row?.id],
    ["card_search_input_id", row?.card_search_input_id],
  ];

  let itemHasStableIdentifier = false;
  if (stableKeys.some(([key, rowValue]) => {
    const itemValue = normalizeLotReviewIdentifier(item?.[key]);
    const normalizedRowValue = normalizeLotReviewIdentifier(rowValue);
    if (itemValue) itemHasStableIdentifier = true;
    return itemValue && normalizedRowValue && itemValue === normalizedRowValue;
  })) {
    return true;
  }

  if (itemHasStableIdentifier) return false;

  const itemName = normalizeLotReviewCardName(item?.card_name || item?.name);
  const rowName = normalizeLotReviewCardName(row?.card_name);
  return Boolean(itemName && rowName && itemName === rowName);
};

const getLotReviewClassificationForRow = (review, row) => {
  if (!review || !row) return [];

  const sources = [
    { classification: "top_value", label: "Top value card", color: "success", items: normalizeLotReviewList(review.top_cards_json) },
    { classification: "risky", label: "Risky card", color: "error", items: normalizeLotReviewList(review.risky_cards_json) },
    { classification: "manual_review", label: "Manual review card", color: "warning", items: normalizeLotReviewList(review.manual_review_cards_json) },
  ];

  return sources.flatMap((source) =>
    source.items
      .filter((item) => lotReviewCardMatchesRow(item, row))
      .map((item) => ({
        classification: source.classification,
        label: source.label,
        color: source.color,
        reason: item.reason,
        suggested_action: item.suggested_action || item.action,
      }))
  );
};

const LotReviewClassificationPanel = ({ classifications }) => (
  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <Typography variant="subtitle2">Lot Review Classification</Typography>
      <Chip label="Advisory only" color="info" variant="outlined" size="small" />
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      Lot review classification is advisory only.
    </Typography>
    {classifications.length > 0 ? (
      <Box sx={{ display: "grid", gap: 1 }}>
        {classifications.map((item, index) => (
          <Box key={`${item.classification}-${index}`}>
            <Chip label={item.label} color={item.color} size="small" sx={{ mr: 1, mb: 0.5 }} />
            {item.suggested_action ? (
              <Chip label={`Action: ${formatLotReviewLabel(item.suggested_action)}`} variant="outlined" size="small" sx={{ mb: 0.5 }} />
            ) : null}
            <Typography variant="body2">
              <strong>Reason:</strong> {item.reason || "No reason provided."}
            </Typography>
          </Box>
        ))}
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary">
        No latest lot review classification found for this card.
      </Typography>
    )}
  </Box>
);

const LotReviewCardList = ({ title, items, emptyText }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title}
    </Typography>
    {items.length > 0 ? (
      <Box sx={{ display: "grid", gap: 1 }}>
        {items.map((item, index) => (
          <Paper key={`${item.card_name || title}-${index}`} variant="outlined" sx={{ p: 1.25 }}>
            <Typography variant="body2">
              <strong>{item.card_name || `Card ${index + 1}`}</strong>
              {item.suggested_action ? ` — ${formatLotReviewLabel(item.suggested_action)}` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.reason || "No reason provided."}
            </Typography>
          </Paper>
        ))}
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    )}
  </Box>
);

const shouldWarnMissingReviews = (rollup) => {
  const totalCards = Number(rollup?.total_cards) || 0;
  const missingReviews = Number(rollup?.unreviewed_cards ?? rollup?.missing_review_count) || 0;
  if (!totalCards || !missingReviews) return false;
  return missingReviews >= 2 || missingReviews / totalCards >= 0.25;
};

const PricingRiskRollupPanel = ({ rollup, loading, error }) => {
  const totalCards = Number(rollup?.total_cards) || 0;
  const reviewedCards = Number(rollup?.reviewed_cards) || 0;
  const missingReviews = Number(rollup?.unreviewed_cards ?? rollup?.missing_review_count) || 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6">Pricing Risk Rollup</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        This summarizes card-level pricing risk reviews. It does not change offer prices or purchase requests.
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading pricing risk rollup...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && rollup ? (
        <>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1.5 }}>
            <Chip label={`Reviewed: ${reviewedCards}/${totalCards}`} variant="outlined" />
            <Chip label={`High Risk: ${rollup.high_risk_count || 0}`} color="error" variant="outlined" />
            <Chip label={`Medium Risk: ${rollup.medium_risk_count || 0}`} color="warning" variant="outlined" />
            <Chip label={`Low Risk: ${rollup.low_risk_count || 0}`} color="success" variant="outlined" />
            <Chip label={`Manual Review: ${rollup.manual_review_required_count || 0}`} variant="outlined" />
            <Chip label={`Missing Review: ${missingReviews}`} variant="outlined" />
          </Box>

          {shouldWarnMissingReviews(rollup) ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Lot review may be less reliable because some cards do not have pricing risk reviews.
            </Alert>
          ) : null}
        </>
      ) : null}

      {!loading && !error && !rollup ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select a purchase request batch to load pricing review coverage.
        </Typography>
      ) : null}
    </Paper>
  );
};

const LotReviewPanel = ({ review, loading, generating, error, onGenerate, disabled }) => {
  const topCards = normalizeLotReviewList(review?.top_cards_json);
  const riskyCards = normalizeLotReviewList(review?.risky_cards_json);
  const manualReviewCards = normalizeLotReviewList(review?.manual_review_cards_json);
  const recommendation = String(review?.overall_recommendation || "").toLowerCase();
  const recommendationColor =
    recommendation === "buy"
      ? "success"
      : recommendation === "negotiate" || recommendation === "cherry_pick"
      ? "warning"
      : recommendation === "pass"
      ? "error"
      : "default";

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
        <Box>
          <Typography variant="h6">LLM Supplier Lot Review</Typography>
          <Typography variant="body2" color="text.secondary">
            This review is advisory only. It does not automatically update offer prices or purchase requests.
          </Typography>
        </Box>
        <Button variant="contained" onClick={onGenerate} disabled={disabled || generating || loading}>
          {generating ? "Generating Lot Review..." : review ? "Regenerate Lot Review" : "Generate Lot Review"}
        </Button>
      </Box>

      {(loading || generating) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">
            {generating ? "Generating supplier lot review..." : "Loading latest supplier lot review..."}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !generating && !review && !error ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No supplier lot review has been generated for this batch yet.
        </Typography>
      ) : null}

      {review && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Chip label={`Recommendation: ${formatLotReviewLabel(review.overall_recommendation)}`} color={recommendationColor} variant="outlined" />
            <Chip label={`Confidence: ${formatLotReviewLabel(review.confidence_level)}`} variant="outlined" />
            <Chip label={`Opening Offer: ${formatCurrency(review.suggested_opening_offer) || "N/A"}`} variant="outlined" />
            <Chip label={`Max Offer: ${formatCurrency(review.suggested_max_offer) || "N/A"}`} variant="outlined" />
          </Box>

          <Typography gutterBottom>
            <strong>Review Summary:</strong> {review.review_summary || "N/A"}
          </Typography>
          <Typography gutterBottom>
            <strong>Negotiation Notes:</strong> {review.negotiation_notes || "N/A"}
          </Typography>
          {review.created_at ? (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Generated At: {review.created_at}
            </Typography>
          ) : null}

          <Divider sx={{ my: 2 }} />
          <LotReviewCardList title="Top Value Cards" items={topCards} emptyText="No top value cards returned." />
          <LotReviewCardList title="Risky Cards" items={riskyCards} emptyText="No risky cards returned." />
          <LotReviewCardList title="Manual Review Cards" items={manualReviewCards} emptyText="No manual review cards returned." />
        </Box>
      )}
    </Paper>
  );
};

const RiskReviewModal = ({ open, onClose, review, loading, generating, error, rowLabel, lotReviewClassifications, onGenerate }) => {
  const recommendedExclusions = normalizeRecommendedExclusions(review);
  const normalizedRisk = String(review?.risk_level || "unknown").toLowerCase();
  const riskColor =
    normalizedRisk === "high"
      ? "error"
      : normalizedRisk === "medium"
      ? "warning"
      : normalizedRisk === "low"
      ? "success"
      : "default";

  return (
    <Dialog open={open} onClose={generating ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>LLM Pricing Risk Review</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          This review is advisory only. It does not automatically exclude sales or change pricing advice.
        </Alert>

        {rowLabel ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Purchase request: {rowLabel}
          </Typography>
        ) : null}

        <LotReviewClassificationPanel classifications={lotReviewClassifications || []} />

        {(loading || generating) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">
              {generating ? "Generating pricing risk review..." : "Loading latest pricing risk review..."}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !generating && !review && !error ? (
          <Typography variant="body2" color="text.secondary">
            No pricing risk review has been generated for this summary yet.
          </Typography>
        ) : null}

        {review && (
          <Box>
            <Box sx={{ mb: 1 }}>
              <Chip label={`Risk: ${normalizedRisk}`} color={riskColor} variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
              <Chip
                label={review.manual_review_required ? "Manual review required" : "Manual review not required"}
                color={review.manual_review_required ? "warning" : "success"}
                variant="outlined"
                size="small"
                sx={{ mb: 1 }}
              />
            </Box>

            <Typography gutterBottom>
              <strong>Review Summary:</strong> {review.review_summary || "N/A"}
            </Typography>
            <Typography gutterBottom>
              <strong>Outlier Notes:</strong> {review.outlier_notes || "N/A"}
            </Typography>
            <Typography gutterBottom>
              <strong>Recommended Price Basis:</strong> {review.recommended_price_basis || "N/A"}
            </Typography>
            <Typography gutterBottom>
              <strong>Buyer Action:</strong> {review.buyer_action || "N/A"}
            </Typography>
            {review.created_at && (
              <Typography gutterBottom>
                <strong>Generated At:</strong> {review.created_at}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography gutterBottom>
              <strong>Recommended Exclusions (advice only):</strong>
            </Typography>
            {recommendedExclusions.length > 0 ? (
              recommendedExclusions.map((item, index) => (
                <Box key={`${item.sales_data_id || "row"}-${index}`} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Sales Row:</strong> {item.sales_data_id || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Reason:</strong> {item.reason || "N/A"}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recommended exclusions returned.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generating}>Close</Button>
        <Button variant="contained" onClick={onGenerate} disabled={generating || loading}>
          {generating ? "Reviewing..." : "Regenerate Review"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function PurchaseRequests() {
  const [viewMode, setViewMode] = useState("batch");
  const [batchId, setBatchId] = useState("");
  const [legacySessionId, setLegacySessionId] = useState("");
  const [batchOptions, setBatchOptions] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);
  const [legacySessionOptions, setLegacySessionOptions] = useState([]);
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [newBatchName, setNewBatchName] = useState("");
  const [batchSessions, setBatchSessions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRows, setModalRows] = useState([]);
  const [poSummary, setPOSummary] = useState(null);
  const [existingModalOpen, setExistingModalOpen] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [bulkPercentAdjustment, setBulkPercentAdjustment] = useState("");
  const [poSummaryOpen, setPOSummaryOpen] = useState(false);
  const [poActionType, setPOActionType] = useState("");
  const [riskReviewModalOpen, setRiskReviewModalOpen] = useState(false);
  const [riskReviewRow, setRiskReviewRow] = useState(null);
  const [riskReview, setRiskReview] = useState(null);
  const [riskReviewLoading, setRiskReviewLoading] = useState(false);
  const [riskReviewGenerating, setRiskReviewGenerating] = useState(false);
  const [riskReviewError, setRiskReviewError] = useState(null);
  const [lotReview, setLotReview] = useState(null);
  const [lotReviewLoading, setLotReviewLoading] = useState(false);
  const [lotReviewGenerating, setLotReviewGenerating] = useState(false);
  const [lotReviewError, setLotReviewError] = useState(null);
  const [pricingRiskRollup, setPricingRiskRollup] = useState(null);
  const [pricingRiskRollupLoading, setPricingRiskRollupLoading] = useState(false);
  const [pricingRiskRollupError, setPricingRiskRollupError] = useState(null);

  const loadBatches = async () => {
    const data = await fetch("/api/purchase-request-batches").then((res) => res.json());
    setBatchOptions(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    fetch("/api/purchase-request-sessions")
      .then((res) => res.json())
      .then((data) => setSessionOptions(data));
    fetch("/api/purchase-requests/legacy-sessions")
      .then((res) => res.json())
      .then((data) => setLegacySessionOptions(Array.isArray(data) ? data : []));
    loadBatches();
  }, []);

  const loadBatchRequests = async (id) => {
    setLoading(true);
    const data = await fetch(`/api/purchase-requests/batch/${id}`).then((res) => res.json());
    setRows(Array.isArray(data) ? data : []);
    setSelectionModel([]);
    setLoading(false);
  };

  const loadLegacyRequests = async (sessionId) => {
    setLoading(true);
    const data = await fetch(`/api/purchase-requests/legacy/${sessionId}`).then((res) => res.json());
    setRows(Array.isArray(data) ? data : []);
    setSelectionModel([]);
    setLoading(false);
  };

  const loadBatchSessions = async (id) => {
    const data = await fetch(`/api/purchase-request-batches/${id}/sessions`).then((res) => res.json());
    const sessions = Array.isArray(data) ? data : [];
    setBatchSessions(sessions);
    setSelectedSessionIds(sessions.map((session) => session.session_id));
  };

  useEffect(() => {
    if (viewMode !== "batch") {
      return;
    }
    if (!batchId) {
      setRows([]);
      setBatchSessions([]);
      setSelectionModel([]);
      setLotReview(null);
      setLotReviewError(null);
      setPricingRiskRollup(null);
      setPricingRiskRollupError(null);
      return;
    }
    loadBatchRequests(batchId);
    loadBatchSessions(batchId);
    fetchLatestLotReview(batchId);
    fetchPricingRiskRollup(batchId);
  }, [batchId, viewMode]);

  useEffect(() => {
    if (viewMode !== "legacy") {
      return;
    }
    setBatchSessions([]);
    if (!legacySessionId) {
      setRows([]);
      setSelectionModel([]);
      return;
    }
    loadLegacyRequests(legacySessionId);
  }, [legacySessionId, viewMode]);

  const selectedBatch = useMemo(
    () => batchOptions.find((batch) => String(batch.id) === String(batchId)),
    [batchId, batchOptions]
  );

  const selectedIdSet = useMemo(() => new Set(selectionModel.map((id) => Number(id))), [selectionModel]);

  const rowsWithDerived = useMemo(
    () => rows.map((row) => ({ ...row, risk_flags: getRiskFlags(row) })),
    [rows]
  );

  const filteredRows = useMemo(() => {
    if (activeFilters.length === 0) {
      return rowsWithDerived;
    }
    return rowsWithDerived.filter((row) => activeFilters.every((filterKey) => matchesFilter(row, filterKey, selectedIdSet)));
  }, [activeFilters, rowsWithDerived, selectedIdSet]);

  const selectedRows = useMemo(
    () => rowsWithDerived.filter((row) => selectedIdSet.has(Number(row.id))),
    [rowsWithDerived, selectedIdSet]
  );

  const summaryFor = (sourceRows) => {
    const selectedCount = sourceRows.length;
    const acceptedCount = sourceRows.filter((row) => row.offer_status === "accepted").length;
    const pendingCount = sourceRows.filter((row) => row.offer_status === "pending").length;
    const rejectedCount = sourceRows.filter((row) => row.offer_status === "rejected").length;
    const alreadyInPOCount = sourceRows.filter((row) => Boolean(row.purchase_order_id)).length;
    const overMaxCount = sourceRows.filter((row) => {
      const offerPrice = toNumber(row.offer_price);
      const maxOfferPrice = toNumber(row.max_offer_price);
      return offerPrice != null && maxOfferPrice != null && offerPrice > maxOfferPrice;
    }).length;
    const missingPricingCount = sourceRows.filter((row) => !row.pricing_advice_id).length;
    const quantityTotal = sourceRows.reduce((sum, row) => sum + rowQuantity(row), 0);
    const offerTotal = sourceRows.reduce((sum, row) => sum + rowOfferTotal(row), 0);
    const predictedTotal = sourceRows.reduce((sum, row) => sum + rowPredictedTotal(row), 0);
    const projectedProfit = predictedTotal - offerTotal;
    const warnings = [];

    if (pendingCount > 0) {
      warnings.push(`${pendingCount} selected row(s) are still pending.`);
    }
    if (rejectedCount > 0) {
      warnings.push(`${rejectedCount} selected row(s) are rejected and should normally be excluded from PO handoff.`);
    }
    if (alreadyInPOCount > 0) {
      warnings.push(`${alreadyInPOCount} selected row(s) are already linked to a purchase order.`);
    }
    if (overMaxCount > 0) {
      warnings.push(`${overMaxCount} selected row(s) are above max offer price.`);
    }
    if (missingPricingCount > 0) {
      warnings.push(`${missingPricingCount} selected row(s) have no pricing advice linked.`);
    }

    return {
      selectedCount,
      acceptedCount,
      pendingCount,
      rejectedCount,
      alreadyInPOCount,
      quantityTotal,
      offerTotal,
      predictedTotal,
      projectedProfit,
      warnings,
    };
  };

  const allSummary = useMemo(() => summaryFor(filteredRows), [filteredRows]);
  const selectedSummary = useMemo(() => summaryFor(selectedRows), [selectedRows]);

  const totalOffer = (rows || []).reduce((sum, row) => sum + rowOfferTotal(row), 0);

  const persistBulkUpdates = async (updatedRows, successMessage) => {
    if (!updatedRows.length) {
      setSnackbar({ open: true, message: "No selected rows to update", severity: "info" });
      return;
    }

    const updatedIds = new Set(updatedRows.map((row) => row.id));
    const nextRows = rows.map((row) => updatedIds.has(row.id) ? (updatedRows.find((updated) => updated.id === row.id) || row) : row);
    setRows(nextRows);

    try {
      const payload = {
        updates: updatedRows.map((row) => ({
          id: row.id,
          offer_price: row.offer_price,
          quantity_ordered: rowQuantity(row),
          offer_status: row.offer_status,
          decision_notes: row.decision_notes,
          decision_by: "admin",
        })),
      };

      const res = await fetch("/api/purchase-requests/bulk-update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.error || "Bulk update failed");
      }
      setSnackbar({ open: true, message: successMessage || result.message || "Bulk update saved", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Failed to save bulk update", severity: "error" });
    }
  };

  const handleChange = async (id, field, value) => {
    const updatedRows = rows.map((row) =>
      row.id === id ? { ...row, [field]: value } : row
    );
    setRows(updatedRows);

    const updatedRow = updatedRows.find((row) => row.id === id);
    const body = {
      id,
      offer_status: updatedRow.offer_status,
      decision_notes: updatedRow.decision_notes,
      offer_price: parseFloat(updatedRow.offer_price),
      quantity_ordered: rowQuantity(updatedRow),
      decision_by: "admin",
    };

    try {
      await fetch("/api/purchase-requests/mark-decision", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setSnackbar({ open: true, message: "Decision saved", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to save decision", severity: "error" });
    }
  };

  const createBatch = async () => {
    if (!newBatchName.trim() || selectedSessionIds.length === 0) {
      setSnackbar({ open: true, message: "Enter a batch name and select at least one session", severity: "warning" });
      return;
    }

    try {
      const res = await fetch("/api/purchase-request-batches", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          batch_name: newBatchName.trim(),
          session_ids: selectedSessionIds,
        }),
      });
      const result = await res.json();
      if (res.ok) {
        await loadBatches();
        setBatchId(String(result.id));
        setNewBatchName("");
        setSelectedSessionIds([]);
        setSnackbar({ open: true, message: "Purchase request batch created", severity: "success" });
      } else {
        setSnackbar({ open: true, message: result.error || "Failed to create batch", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to create batch", severity: "error" });
    }
  };

  const updateBatchSessions = async () => {
    if (!batchId || selectedSessionIds.length === 0) {
      setSnackbar({ open: true, message: "Choose a batch and at least one session", severity: "warning" });
      return;
    }

    try {
      const res = await fetch(`/api/purchase-request-batches/${batchId}/sessions`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_ids: selectedSessionIds }),
      });
      const result = await res.json();
      if (res.ok) {
        await loadBatches();
        await loadBatchSessions(batchId);
        setSnackbar({ open: true, message: result.message || "Batch sessions updated", severity: "success" });
      } else {
        setSnackbar({ open: true, message: result.error || "Failed to update batch sessions", severity: "warning" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to update batch sessions", severity: "error" });
    }
  };

  const addSessionsToBatch = async () => {
    if (!batchId || selectedSessionIds.length === 0) {
      setSnackbar({ open: true, message: "Choose a batch and select at least one session", severity: "warning" });
      return;
    }

    const existingIds = new Set((batchSessions || []).map((session) => session.session_id));
    const newSessionIds = selectedSessionIds.filter((sessionId) => !existingIds.has(sessionId));
    if (newSessionIds.length === 0) {
      setSnackbar({ open: true, message: "No new sessions selected to add to this batch", severity: "info" });
      return;
    }

    try {
      const res = await fetch(`/api/purchase-request-batches/${batchId}/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_ids: newSessionIds }),
      });
      const result = await res.json();
      if (res.ok) {
        await loadBatches();
        await loadBatchSessions(batchId);
        setSnackbar({ open: true, message: result.message || "Sessions added to batch", severity: "success" });
      } else {
        setSnackbar({ open: true, message: result.error || "Failed to add sessions to batch", severity: "warning" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to add sessions to batch", severity: "error" });
    }
  };

  const generatePurchaseRequests = async () => {
    try {
      const res = await fetch(`/api/purchase-request-batches/${batchId}/generate`, { method: "POST" });
      const result = await res.json();
      if (res.ok) {
        setSnackbar({ open: true, message: result.message, severity: result.message?.includes("No new purchase requests") ? "info" : "success" });
        loadBatchRequests(batchId);
        loadBatches();
      } else {
        setSnackbar({ open: true, message: result.error || "Error", severity: "warning" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Request failed", severity: "error" });
    }
  };

  const handleAddCard = () => {
    const newCard = {
      id: rows.length + 1,
      card_name: "",
      predicted_sale_price: "",
      suggested_buy_price: "",
      max_offer_price: "",
      offer_price: "",
      quantity_ordered: 1,
      sourcing_score: "",
      confidence_score: "",
      offer_status: "pending",
      decision_notes: "",
      risk_flags: ["Missing Summary", "Missing Pricing", "Needs Decision"],
    };
    setRows([...rows, newCard]);
  };

  const handleAcceptAll = async () => {
    if (!rows.length) return;
    const updatedRows = rows.map((row) => ({ ...row, offer_status: "accepted" }));
    await persistBulkUpdates(updatedRows, "All purchase requests set to Accepted");
  };

  const applyStatusToSelected = async (status) => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: "Select one or more rows first", severity: "info" });
      return;
    }
    const updatedRows = selectedRows.map((row) => ({ ...row, offer_status: status }));
    await persistBulkUpdates(updatedRows, `Selected rows set to ${status}`);
  };

  const capSelectedAtMaxOffer = async () => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: "Select one or more rows first", severity: "info" });
      return;
    }

    const updatedRows = selectedRows.map((row) => {
      const offerPrice = toNumber(row.offer_price);
      const maxOfferPrice = toNumber(row.max_offer_price);
      if (offerPrice == null || maxOfferPrice == null) {
        return row;
      }
      return {
        ...row,
        offer_price: offerPrice > maxOfferPrice ? roundToTwo(maxOfferPrice) : roundToTwo(offerPrice),
      };
    });

    await persistBulkUpdates(updatedRows, "Selected offers capped at max offer where applicable");
  };

  const roundSelectedOffers = async () => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: "Select one or more rows first", severity: "info" });
      return;
    }

    const updatedRows = selectedRows.map((row) => {
      const offerPrice = toNumber(row.offer_price);
      return offerPrice == null ? row : { ...row, offer_price: roundToTwo(offerPrice) };
    });

    await persistBulkUpdates(updatedRows, "Selected offers rounded to 2 decimals");
  };

  const applySuggestedBuyToSelected = async () => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: "Select one or more rows first", severity: "info" });
      return;
    }

    const applicableRows = selectedRows.filter((row) => toNumber(row.suggested_buy_price) != null);
    if (!applicableRows.length) {
      setSnackbar({ open: true, message: "No selected rows have a suggested buy price", severity: "warning" });
      return;
    }

    const updatedRows = applicableRows.map((row) => ({
      ...row,
      offer_price: roundToTwo(toNumber(row.suggested_buy_price)),
    }));

    await persistBulkUpdates(updatedRows, `Applied Suggested Buy to ${updatedRows.length} selected row(s)`);
  };

  const applyMaxOfferToSelected = async () => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: "Select one or more rows first", severity: "info" });
      return;
    }

    const applicableRows = selectedRows.filter((row) => toNumber(row.max_offer_price) != null);
    if (!applicableRows.length) {
      setSnackbar({ open: true, message: "No selected rows have a max offer price", severity: "warning" });
      return;
    }

    const updatedRows = applicableRows.map((row) => ({
      ...row,
      offer_price: roundToTwo(toNumber(row.max_offer_price)),
    }));

    await persistBulkUpdates(updatedRows, `Applied Max Offer to ${updatedRows.length} selected row(s)`);
  };

  const applyPercentAdjustment = async () => {
    const percent = Number(bulkPercentAdjustment);
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: "Select one or more rows first", severity: "info" });
      return;
    }
    if (!Number.isFinite(percent)) {
      setSnackbar({ open: true, message: "Enter a valid percent adjustment", severity: "warning" });
      return;
    }

    const multiplier = 1 + percent / 100;
    const updatedRows = selectedRows.map((row) => {
      const offerPrice = toNumber(row.offer_price);
      return offerPrice == null ? row : { ...row, offer_price: roundToTwo(offerPrice * multiplier) };
    });

    await persistBulkUpdates(updatedRows, `Adjusted selected offers by ${percent}%`);
  };

  const selectedLegacySession = useMemo(
    () => legacySessionOptions.find((session) => String(session.session_id) === String(legacySessionId)),
    [legacySessionId, legacySessionOptions]
  );

  const toggleFilter = (filterKey) => {
    setActiveFilters((current) =>
      current.includes(filterKey) ? current.filter((item) => item !== filterKey) : [...current, filterKey]
    );
  };

  const openPrePOSummary = (actionType) => {
    if (actionType === "create") {
      const contextRows = rowsWithDerived.filter((row) => !row.purchase_order_id);
      const pendingRows = contextRows.filter((row) => row.offer_status === "pending");
      if (pendingRows.length > 0) {
        setSnackbar({
          open: true,
          message: `Cannot create PO while ${pendingRows.length} row(s) are still pending. Accept or reject all remaining cards first.`,
          severity: "warning",
        });
        return;
      }

      const acceptedRows = contextRows.filter((row) => row.offer_status === "accepted");
      if (acceptedRows.length === 0) {
        setSnackbar({ open: true, message: "No accepted rows available to create a PO.", severity: "warning" });
        return;
      }

      setModalRows(acceptedRows);
      setPOSummary(summaryFor(acceptedRows));
      setPOActionType(actionType);
      setPOSummaryOpen(true);
      return;
    }

    const eligibleRows = selectedRows.filter((row) => row.offer_status === "accepted" && !row.purchase_order_id);
    if (eligibleRows.length === 0) {
      setSnackbar({ open: true, message: "Select one or more accepted rows that are not already in a PO.", severity: "warning" });
      return;
    }
    setModalRows(eligibleRows);
    setPOSummary(summaryFor(eligibleRows));
    setPOActionType(actionType);
    setPOSummaryOpen(true);
  };

  const continueFromPOSummary = () => {
    setPOSummaryOpen(false);
    if (poActionType === "create") {
      setModalOpen(true);
      return;
    }
    if (poActionType === "existing") {
      setExistingModalOpen(true);
    }
  };

  const fetchPricingRiskRollup = async (id) => {
    if (!id) return;
    setPricingRiskRollupLoading(true);
    setPricingRiskRollupError(null);
    try {
      const res = await fetch(`/api/llm-pricing-reviews/batch/${id}/rollup`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to load pricing risk rollup");
      setPricingRiskRollup(json || null);
    } catch (err) {
      console.error(err);
      setPricingRiskRollupError(err.message || "Failed to load pricing risk rollup");
    } finally {
      setPricingRiskRollupLoading(false);
    }
  };

  const fetchLatestLotReview = async (id) => {
    if (!id) return;
    setLotReviewLoading(true);
    setLotReviewError(null);
    try {
      const res = await fetch(`/api/llm-lot-reviews/batch/${id}/latest`);
      const json = await res.json();
      if (res.status === 404) {
        setLotReview(null);
        return;
      }
      if (!res.ok) throw new Error(json.error || "Failed to load supplier lot review");
      setLotReview(json.review || null);
    } catch (err) {
      console.error(err);
      setLotReviewError(err.message || "Failed to load supplier lot review");
    } finally {
      setLotReviewLoading(false);
    }
  };

  const generateLotReview = async () => {
    if (!batchId) {
      setLotReviewError("Supplier lot review requires a selected purchase request batch.");
      return;
    }
    setLotReviewGenerating(true);
    setLotReviewError(null);
    try {
      const res = await fetch(`/api/llm-lot-reviews/batch/${batchId}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ created_by: "purchase_request_batch_view" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to generate supplier lot review");
      setLotReview(json.review || null);
      setSnackbar({ open: true, message: "Supplier lot review generated", severity: "success" });
    } catch (err) {
      console.error(err);
      setLotReviewError(err.message || "Failed to generate supplier lot review");
      setSnackbar({ open: true, message: err.message || "Failed to generate supplier lot review", severity: "error" });
    } finally {
      setLotReviewGenerating(false);
    }
  };

  const fetchLatestRiskReview = async (summaryId) => {
    setRiskReviewLoading(true);
    setRiskReviewError(null);
    try {
      const res = await fetch(`/api/llm-pricing-reviews/summary/${summaryId}/latest`);
      const json = await res.json();
      if (res.status === 404) {
        setRiskReview(null);
        return;
      }
      if (!res.ok) throw new Error(json.error || "Failed to load pricing risk review");
      setRiskReview(json.review || null);
    } catch (err) {
      console.error(err);
      setRiskReviewError(err.message || "Failed to load pricing risk review");
    } finally {
      setRiskReviewLoading(false);
    }
  };

  const generateRiskReview = async (summaryId) => {
    setRiskReviewGenerating(true);
    setRiskReviewError(null);
    try {
      const res = await fetch(`/api/llm-pricing-reviews/summary/${summaryId}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ created_by: "purchase_request_batch_grid" }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to generate pricing risk review");
      setRiskReview(json.review || null);
      if (batchId) fetchPricingRiskRollup(batchId);
      setSnackbar({ open: true, message: "Pricing risk review generated", severity: "success" });
    } catch (err) {
      console.error(err);
      setRiskReviewError(err.message || "Failed to generate pricing risk review");
      setSnackbar({ open: true, message: err.message || "Failed to generate pricing risk review", severity: "error" });
    } finally {
      setRiskReviewGenerating(false);
    }
  };

  const handleOpenRiskReview = async (row) => {
    if (!row.summary_id) {
      setSnackbar({ open: true, message: "Pricing risk review requires a linked summary record.", severity: "warning" });
      return;
    }
    setRiskReviewRow(row);
    setRiskReview(null);
    setRiskReviewError(null);
    setRiskReviewModalOpen(true);
    await fetchLatestRiskReview(row.summary_id);
    await generateRiskReview(row.summary_id);
  };

  const handleRegenerateRiskReview = async () => {
    if (!riskReviewRow?.summary_id) {
      setRiskReviewError("Pricing risk review requires a linked summary record.");
      return;
    }
    await generateRiskReview(riskReviewRow.summary_id);
  };

  const closeRiskReviewModal = () => {
    setRiskReviewModalOpen(false);
    setRiskReviewRow(null);
    setRiskReview(null);
    setRiskReviewError(null);
    setRiskReviewLoading(false);
    setRiskReviewGenerating(false);
  };

  const columns = [
    {
      field: "pricing_risk_review",
      headerName: "Pricing Risk",
      width: 170,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const hasSummary = Boolean(params.row.summary_id);
        return (
          <Button
            size="small"
            variant="outlined"
            disabled={!hasSummary || riskReviewGenerating}
            title={hasSummary ? "Generate and view LLM pricing risk review" : "Pricing risk review requires a linked summary record."}
            onClick={(event) => {
              event.stopPropagation();
              handleOpenRiskReview(params.row);
            }}
          >
            Review Pricing Risk
          </Button>
        );
      },
    },
    {
      field: "lot_review_classification",
      headerName: "Lot Review",
      width: 190,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const classifications = getLotReviewClassificationForRow(lotReview, params.row);
        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.5 }}>
            {classifications.length > 0 ? classifications.slice(0, 2).map((item) => (
              <Chip key={item.classification} label={item.label} color={item.color} size="small" variant="outlined" />
            )) : (
              <Typography variant="caption" color="text.secondary">No lot tag</Typography>
            )}
          </Box>
        );
      },
    },
    { field: "search_session_id", headerName: "Session ID", width: 100 },
    {
      field: "risk_flags",
      headerName: "Risk Flags",
      width: 260,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.5 }}>
          {(params.row.risk_flags || []).slice(0, 3).map((flag) => (
            <Chip key={flag} label={flag} size="small" color={flag.includes("Over") ? "warning" : flag.includes("Missing") ? "default" : "info"} />
          ))}
        </Box>
      ),
    },
    {
      field: "sell_through_rate_30",
      headerName: "Sell-through 30d",
      width: 140,
      renderCell: (params) => {
        const directValue = params.row.sell_through_rate_30;
        if (directValue != null && directValue !== "") {
          return `${Number(directValue).toFixed(2)}%`;
        }

        const soldCount = Number(params.row.sales_count_30_actual);
        const activeCount = Number(params.row.active_listings_count);

        if (!Number.isNaN(soldCount) && !Number.isNaN(activeCount)) {
          const total = soldCount + activeCount;
          if (total > 0) {
            return `${((soldCount / total) * 100).toFixed(2)}%`;
          }
        }

        return "";
      },
    },
    { field: "card_name", headerName: "Card", width: 220 },
    {
      field: "predicted_sale_price",
      headerName: "Predicted",
      width: 110,
      renderCell: (params) => formatCurrency(params.row.predicted_sale_price),
    },
    {
      field: "offer_price",
      headerName: "Unit Offer",
      width: 110,
      renderCell: (params) => (
        <TextField
          fullWidth
          value={params.row.offer_price || ""}
          onChange={(e) => handleChange(params.row.id, "offer_price", e.target.value)}
        />
      ),
    },
    {
      field: "quantity_ordered",
      headerName: "Qty",
      width: 90,
      renderCell: (params) => (
        <TextField
          fullWidth
          type="number"
          value={rowQuantity(params.row)}
          inputProps={{ min: 1, step: 1 }}
          onChange={(e) => handleChange(params.row.id, "quantity_ordered", e.target.value)}
        />
      ),
    },
    {
      field: "offer_total",
      headerName: "Offer Total",
      width: 120,
      renderCell: (params) => formatCurrency(rowOfferTotal(params.row)),
    },
    {
      field: "offer_status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <FormControl fullWidth>
          <Select
            value={params.row.offer_status}
            onChange={(e) => handleChange(params.row.id, "offer_status", e.target.value)}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      ),
    },
    {
      field: "decision_notes",
      headerName: "Notes",
      width: 250,
      renderCell: (params) => (
        <TextField
          fullWidth
          value={params.row.decision_notes || ""}
          onChange={(e) => handleChange(params.row.id, "decision_notes", e.target.value)}
        />
      ),
    },
    {
      field: "suggested_buy_price",
      headerName: "Buy Price",
      width: 110,
      renderCell: (params) => formatCurrency(params.row.suggested_buy_price),
    },
    {
      field: "calculated_price_90",
      headerName: "Calc 90d",
      width: 110,
      renderCell: (params) => {
        const value = params.row.calculated_price_90;
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      field: "merged_calculated_price",
      headerName: "Merged Calc",
      width: 120,
      renderCell: (params) => {
        const value = params.row.merged_calculated_price;
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      field: "suggested_buy_price_90",
      headerName: "Buy 90d",
      width: 110,
      renderCell: (params) => {
        const value = params.row.suggested_buy_price_90;
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      field: "merged_suggested_buy_price",
      headerName: "Merged Buy",
      width: 120,
      renderCell: (params) => {
        const value = params.row.merged_suggested_buy_price;
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      field: "active_listings_count",
      headerName: "Active Cnt",
      width: 110,
      renderCell: (params) => (params.row.active_listings_count != null ? params.row.active_listings_count : ""),
    },
    {
      field: "active_low_price_no_shipping",
      headerName: "Active Low",
      width: 110,
      renderCell: (params) => {
        const value = params.row.active_low_price_no_shipping;
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      field: "active_median_price_no_shipping",
      headerName: "Active Median",
      width: 120,
      renderCell: (params) => {
        const value = params.row.active_median_price_no_shipping;
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      field: "active_average_price_no_shipping",
      headerName: "Active Avg",
      width: 110,
      renderCell: (params) => {
        const value = params.row.active_average_price_no_shipping;
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      field: "max_offer_price",
      headerName: "Max Offer",
      width: 110,
      renderCell: (params) => formatCurrency(params.row.max_offer_price),
    },
    {
      field: "sourcing_score",
      headerName: "Score",
      width: 100,
      renderCell: (params) => formatPercent(params.row.sourcing_score),
    },
    {
      field: "confidence_score",
      headerName: "Confidence",
      width: 130,
      renderCell: (params) => formatPercent(params.row.confidence_score),
    },
    {
      field: "elite_predicted_sale_price",
      headerName: "Elite Predicted",
      width: 130,
      renderCell: (params) => {
        const value = params.row.elite_predicted_sale_price;
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      field: "elite_suggested_buy_price",
      headerName: "Elite Suggested",
      width: 140,
      renderCell: (params) => {
        const value = params.row.elite_suggested_buy_price;
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      field: "elite_sourcing_score",
      headerName: "Elite Score %",
      width: 130,
      renderCell: (params) => {
        const value = params.row.elite_sourcing_score;
        return value != null ? `${Number(value).toFixed(1)}%` : "";
      },
    },
    {
      field: "elite_profitable_to_source",
      headerName: "Elite OK",
      width: 110,
      renderCell: (params) => (params.value ? "✅" : "❌"),
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Purchase Requests
      </Typography>

      <Tabs
        value={viewMode}
        onChange={(_, value) => {
          setViewMode(value);
          setRows([]);
          setSelectionModel([]);
        }}
        sx={{ mb: 2 }}
      >
        <Tab value="batch" label="Batch Purchase Requests" />
        <Tab value="legacy" label="Legacy Purchase Requests" />
      </Tabs>

      {viewMode === "batch" && (
      <>
      <Box sx={{ display: "grid", gap: 2, mb: 3 }}>
        <Typography variant="h6">Create or Maintain Purchase Request Batch</Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="New Batch Name"
            value={newBatchName}
            onChange={(e) => setNewBatchName(e.target.value)}
            sx={{ minWidth: 280 }}
          />

          <FormControl sx={{ minWidth: 360 }}>
            <InputLabel>Select Sessions</InputLabel>
            <Select
              multiple
              value={selectedSessionIds}
              onChange={(e) => setSelectedSessionIds(e.target.value)}
              input={<OutlinedInput label="Select Sessions" />}
              renderValue={(selected) =>
                selected
                  .map((id) => sessionOptions.find((option) => option.session_id === id)?.session_name || `Session ${id}`)
                  .join(", ")
              }
              MenuProps={MENU_PROPS}
            >
              {sessionOptions.map((session) => (
                <MenuItem
                  key={session.session_id}
                  value={session.session_id}
                  disabled={session.is_selectable_for_batch === false}
                >
                  {session.session_name} (ID: {session.session_id})
                  {session.is_selectable_for_batch === false && session.ineligible_reason
                    ? ` — ${session.ineligible_reason}`
                    : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
            Sessions with existing legacy purchase requests or another batch link are disabled. Existing batch sessions can be kept and new eligible sessions can be added.
          </Typography>

          <Button variant="contained" onClick={createBatch}>
            Create Batch
          </Button>

          <Button variant="outlined" disabled={!batchId} onClick={addSessionsToBatch}>
            Add Sessions To Batch
          </Button>

          <Button variant="outlined" disabled={!batchId} onClick={updateBatchSessions}>
            Replace Batch Sessions
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <FormControl sx={{ minWidth: 360 }}>
          <InputLabel>Select Batch</InputLabel>
          <Select
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            label="Select Batch"
          >
            <MenuItem value="" disabled>Select a batch</MenuItem>
            {batchOptions.map((batch) => (
              <MenuItem key={batch.id} value={String(batch.id)}>
                {batch.batch_name} (ID: {batch.id})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" disabled={viewMode !== "batch" || !batchId} onClick={generatePurchaseRequests}>
          Generate Purchase Requests
        </Button>

        <Button variant="outlined" disabled={viewMode !== "batch" || !batchId || lotReviewGenerating || lotReviewLoading} onClick={generateLotReview}>
          {lotReviewGenerating ? "Generating Lot Review..." : "Generate Lot Review"}
        </Button>

        <Button variant="contained" disabled={viewMode !== "batch" || !batchId || rows.length === 0} onClick={handleAcceptAll}>
          Accept All
        </Button>

        <Button
          variant="outlined"
          disabled={viewMode !== "batch" || !batchId || rows.length === 0}
          onClick={() => openPrePOSummary("create")}
        >
          Create New PO
        </Button>

        <Button
          variant="outlined"
          disabled={viewMode !== "batch" || !batchId || rows.length === 0}
          onClick={() => openPrePOSummary("existing")}
        >
          Add to Existing PO
        </Button>
      </Box>

      {selectedBatch && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">
            Batch: {selectedBatch.batch_name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Sessions linked: {selectedBatch.session_count || 0} | Requests generated: {selectedBatch.request_count || 0}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {batchSessions.map((session) => (
              <Chip key={session.session_id} label={`${session.session_name} (${session.session_id})`} size="small" />
            ))}
          </Box>
        </Box>
      )}

      {batchId && (
        <>
          <PricingRiskRollupPanel
            rollup={pricingRiskRollup}
            loading={pricingRiskRollupLoading}
            error={pricingRiskRollupError}
          />
          <LotReviewPanel
            review={lotReview}
            loading={lotReviewLoading}
            generating={lotReviewGenerating}
            error={lotReviewError}
            onGenerate={generateLotReview}
            disabled={viewMode !== "batch" || !batchId}
          />
        </>
      )}
      </>
      )}

      {viewMode === "legacy" && (
        <Box sx={{ display: "grid", gap: 2, mb: 3 }}>
          <Typography variant="h6">View Legacy Purchase Requests</Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl sx={{ minWidth: 360 }}>
              <InputLabel>Select Legacy Session</InputLabel>
              <Select
                value={legacySessionId}
                onChange={(e) => setLegacySessionId(e.target.value)}
                label="Select Legacy Session"
              >
                <MenuItem value="" disabled>Select a legacy session</MenuItem>
                {legacySessionOptions.map((session) => (
                  <MenuItem key={session.session_id} value={String(session.session_id)}>
                    {(session.session_name || `Session ${session.session_id}`)} (ID: {session.session_id}) - {session.request_count} rows
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedLegacySession && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1">
                Legacy Session: {selectedLegacySession.session_name || `Session ${selectedLegacySession.session_id}`}
              </Typography>
              <Typography variant="body2">
                Session ID: {selectedLegacySession.session_id} | Legacy rows: {selectedLegacySession.request_count || 0}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button variant="outlined" onClick={handleAddCard} disabled={viewMode !== "batch"}>Add Card</Button>
      </Box>

      {rows.length > 0 && (
        <>
          <Box sx={{ mb: 2, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TotalsCard label="Visible Rows" value={filteredRows.length} helperText={`Loaded: ${rows.length}`} />
            <TotalsCard label="Selected Rows" value={selectedSummary.selectedCount} helperText={`Qty: ${selectedSummary.quantityTotal}`} />
            <TotalsCard label="Visible Offer Total" value={formatCurrency(allSummary.offerTotal)} helperText={`Loaded total: ${formatCurrency(totalOffer)}`} />
            <TotalsCard label="Selected Offer Total" value={formatCurrency(selectedSummary.offerTotal)} />
            <TotalsCard label="Selected Predicted Total" value={formatCurrency(selectedSummary.predictedTotal)} />
            <TotalsCard label="Selected Projected Profit" value={formatCurrency(selectedSummary.projectedProfit)} />
            <TotalsCard label="Accepted Selected" value={selectedSummary.acceptedCount} />
            <TotalsCard label="Pending Selected" value={selectedSummary.pendingCount} />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Review Filters
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {FILTER_DEFINITIONS.map((filter) => (
                <Chip
                  key={filter.key}
                  label={filter.label}
                  clickable
                  color={activeFilters.includes(filter.key) ? "primary" : "default"}
                  variant={activeFilters.includes(filter.key) ? "filled" : "outlined"}
                  onClick={() => toggleFilter(filter.key)}
                />
              ))}
              {activeFilters.length > 0 && (
                <Button size="small" onClick={() => setActiveFilters([])}>
                  Clear Filters
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Selected Row Bulk Actions
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Button variant="outlined" onClick={() => applyStatusToSelected("accepted")}>Accept Selected</Button>
              <Button variant="outlined" onClick={() => applyStatusToSelected("pending")}>Set Pending</Button>
              <Button variant="outlined" onClick={() => applyStatusToSelected("rejected")}>Reject Selected</Button>
              <Button variant="outlined" onClick={applySuggestedBuyToSelected}>Use Suggested Buy</Button>
              <Button variant="outlined" onClick={applyMaxOfferToSelected}>Use Max Offer</Button>
              <Button variant="outlined" onClick={capSelectedAtMaxOffer}>Cap To Max Offer</Button>
              <Button variant="outlined" onClick={roundSelectedOffers}>Round Offers</Button>
              <TextField
                label="Adjust %"
                value={bulkPercentAdjustment}
                onChange={(e) => setBulkPercentAdjustment(e.target.value)}
                size="small"
                sx={{ width: 120 }}
              />
              <Button variant="outlined" onClick={applyPercentAdjustment}>Apply %</Button>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <CSVLink data={filteredRows} filename={viewMode === "batch" ? `purchase_requests_batch_${batchId}.csv` : `purchase_requests_legacy_session_${legacySessionId}.csv`}>
              <Button variant="outlined">Export to CSV</Button>
            </CSVLink>
          </Box>
        </>
      )}

      {((viewMode === "batch" && batchId) || (viewMode === "legacy" && legacySessionId)) && rows.length > 0 && (
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pageSize={10}
          autoHeight
          checkboxSelection
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          rowSelectionModel={{ type: "include", ids: new Set(selectionModel) }}
          onRowSelectionModelChange={(newModel) => {
            setSelectionModel(Array.from(newModel?.ids ?? []));
          }}
        />
      )}

      {viewMode === "batch" && batchId && rows.length === 0 && !loading && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          No purchase requests found for this batch.
        </Typography>
      )}

      {viewMode === "legacy" && legacySessionId && rows.length === 0 && !loading && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          No legacy purchase requests found for this session.
        </Typography>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <PrePOSummaryDialog
        open={poSummaryOpen}
        onClose={() => setPOSummaryOpen(false)}
        onConfirm={continueFromPOSummary}
        actionLabel={poActionType === "create" ? "Pre-PO Summary: Create New PO" : "Pre-PO Summary: Add to Existing PO"}
        summary={poSummary || selectedSummary}
      />

      <RiskReviewModal
        open={riskReviewModalOpen}
        onClose={closeRiskReviewModal}
        review={riskReview}
        loading={riskReviewLoading}
        generating={riskReviewGenerating}
        error={riskReviewError}
        rowLabel={riskReviewRow?.card_name || (riskReviewRow?.id ? `Row ${riskReviewRow.id}` : "")}
        lotReviewClassifications={getLotReviewClassificationForRow(lotReview, riskReviewRow)}
        onGenerate={handleRegenerateRiskReview}
      />

      <CreatePOFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedRows={modalRows}
        batchId={batchId}
        sessionId={viewMode === "legacy" ? legacySessionId : ""}
        summary={summaryFor(modalRows)}
        onCreated={(id) => {
          console.log("PO created with ID:", id);
          setModalOpen(false);
        }}
      />

      <AddToExistingPOModal
        open={existingModalOpen}
        onClose={() => setExistingModalOpen(false)}
        batchId={batchId}
        selectedRows={modalRows}
        summary={summaryFor(modalRows)}
      />
    </Paper>
  );
}
